import { useState } from 'react';
import type { NotificationPref } from '@xdocs/schemas';
import { updateNotificationPrefsInput } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

/**
 * F10 Notifications-prefs section. `settings.updateNotificationPrefs` is a
 * shipped op (`packages/schemas/src/ws.ts`) but is NOT (yet) entered into
 * `WsOpContracts` (`packages/ws-protocol/src/op-contracts.ts` — out of this
 * task's ownership), so it can't go through `requestTyped`; called via the
 * raw `WsClient.request()` escape hatch instead, validating the payload
 * against the shared `updateNotificationPrefsInput` schema client-side
 * before sending (same discipline `requestTyped` would give for free).
 *
 * KNOWN GAP (flagged in WIRING.md): there is no paired READ op (no
 * `settings.getNotificationPrefs`/equivalent anywhere in the frozen
 * contract), so this hook cannot hydrate from the user's actually-saved
 * preferences — it always starts every mount from `DEFAULT_PREFS` (all
 * channels on). Saving still round-trips through the real op and DOES
 * persist server-side; only the pre-fill is unavailable.
 */
export const NOTIFICATION_TYPES = ['job.done', 'comment.new', 'reaction.new', 'share.approved', 'credit.low'] as const;
export type NotificationPrefType = (typeof NOTIFICATION_TYPES)[number];

function defaultPrefs(): NotificationPref[] {
  return NOTIFICATION_TYPES.map((type) => ({ type, inApp: true, webPush: true, email: true }));
}

export interface UseNotificationPrefsResult {
  prefs: NotificationPref[];
  setChannel: (type: NotificationPrefType, channel: 'inApp' | 'webPush' | 'email', value: boolean) => void;
  save: () => Promise<void>;
  isSaving: boolean;
  saveError: string | undefined;
}

export function useNotificationPrefs(): UseNotificationPrefsResult {
  const { wsClient } = useWs();
  const [prefs, setPrefs] = useState<NotificationPref[]>(() => defaultPrefs());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  function setChannel(type: NotificationPrefType, channel: 'inApp' | 'webPush' | 'email', value: boolean): void {
    setPrefs((prev) => prev.map((pref) => (pref.type === type ? { ...pref, [channel]: value } : pref)));
  }

  async function save(): Promise<void> {
    setIsSaving(true);
    setSaveError(undefined);
    try {
      const payload = updateNotificationPrefsInput.parse({ prefs });
      await wsClient.request('settings.updateNotificationPrefs', payload);
    } catch {
      setSaveError('errors:code.INTERNAL');
      throw new Error('settings.updateNotificationPrefs failed');
    } finally {
      setIsSaving(false);
    }
  }

  return { prefs, setChannel, save, isSaving, saveError };
}
